import { defineStore } from 'pinia'
import type { Undefinable } from 'ts-helpers'
import { computed, ref } from 'vue'
import { partnerKey, useConfiguration } from '../../Configuration'
import { type Location, useLocation } from '../../Location'
import { userService } from '../api'
import { userAuthToken, userId as userHelper } from '../lib'
import {
  type User,
  UserAdapter,
  type UserDTO,
  UserDTOAdapter,
  type UserEncryptedParams
} from '../model'

export const useUser = defineStore('user', () => {
  // user
  const defaultUser = new UserAdapter({ userId: '' })
  const user = ref<Undefinable<User>>(defaultUser)

  const getUser = async (): Promise<UserDTO> => {
    return await userService.getUser()
  }

  const authUser = async (userData: UserDTO | UserEncryptedParams) => {
    const setUserData = (userData: UserDTO) => {
      setUser(userData)

      return userData
    }

    try {
      return getUser()
        .then((userData: UserDTO) => {
          if (userData.partnerKey !== partnerKey.get()) {
            throw new Error('User key dont match with partnerKey')
          }

          return setUserData(userData)
        })
        .catch(async () => {
          try {
            const userDTO = await userService.authUser(userData)

            return setUserData(userDTO)
          } catch (error: any) {
            if (error.status === 400) partnerKey.handleEmpty()
          }
        })
    } catch (error) {
      setDefaultUser()
      console.log('error to fetch user ', error)
    }
  }

  const refreshUser = async () => {
    const userDTO = await userService.authUser(new UserDTOAdapter(user.value || defaultUser))

    setUser(userDTO)
  }

  const setUserAuthToken = (token: string) => {
    userAuthToken.set(token)
  }

  const setDefaultUser = () => {
    user.value = defaultUser
    userHelper.set(defaultUser.userId)
  }

  const setUser = (userDTO: UserDTO) => {
    user.value = new UserAdapter(userDTO)
    userDTO.authToken && setUserAuthToken(userDTO.authToken)
    userHelper.set(userDTO.userId)
  }

  const changeUserProp = <T extends keyof User>(prop: T, value: User[T]) => {
    user.value && (user.value[prop] = value)
  }

  const updateUser = async (data?: Partial<UserDTO>, updatePage: boolean = true) => {
    const { updateAppOnCriticalParamsChange } = useConfiguration()

    try {
      const { currentLocation } = useLocation()

      if (!user.value) return
      const userDTO = new UserDTOAdapter({
        ...user.value,
        location: currentLocation!.id,
        ...data
      })

      if (updatePage) {
        await updateAppOnCriticalParamsChange<UserDTO>(userService.updateUser(userDTO))
      } else {
        await userService.updateUser(userDTO)
      }
    } catch (error) {
      console.log('error to update user ', error)
    }
  }

  //sex
  const userSex = computed(() => user.value && user.value.sex)
  const updateUserSex = (sex: User['sex']) => {
    changeUserProp<'sex'>('sex', sex)

    updateUser()
  }

  //location
  const updateUserLocation = async (location: Location, needToUpdatePageOutside?: boolean) => {
    const { currentLocation } = useLocation()

    const needToUpdatePage =
      needToUpdatePageOutside ?? !!(currentLocation?.id && location.id !== currentLocation.id)
    await updateUser({ location: location.id, adjustLocation: false }, needToUpdatePage)
  }

  return {
    //base
    user,
    authUser,
    updateUser,
    refreshUser,
    //sex
    userSex,
    updateUserSex,
    // location
    updateUserLocation
  }
})
